import { UserLocation } from "@store/generalTypes";
import { getEnumKeyByValue } from "@helpers/enumHelper";

export interface Address {
    streetAddress: string;
    streetAddress2?: string;
    city: string;
    stateCode: string;
    postalCode: string;
    country?: string;
}

export interface Organization {
    id?: number;
    npiNumber?: string;
    orgName: string;
    address: Address;
    primaryContact?: PersonalInfo;
    administrator?: PersonalInfo;
    agreeTerms?: boolean;
    agreeTermsStamp?: Date;
    userId?: string;
    activeStaffUsers?: number;
    inactiveStaffUsers?: number;
    status: OrganizationStatus;
    details?: string;
    locationId?: number;
}

export enum OrganizationStatus {
    active = "Active",
    inactive = "Inactive",
    purchaseIncomplete = "Purchase Incomplete",
    accessRevoked = "Access Revoked",
    deleted = "Deleted"
}

export const apiOrganizationStatus = {
    [OrganizationStatus.active]: getEnumKeyByValue(OrganizationStatus, OrganizationStatus.active)!,
    [OrganizationStatus.inactive]: getEnumKeyByValue(OrganizationStatus, OrganizationStatus.inactive)!,
    [OrganizationStatus.purchaseIncomplete]: getEnumKeyByValue(
        OrganizationStatus,
        OrganizationStatus.purchaseIncomplete
    )!,
    [OrganizationStatus.accessRevoked]: getEnumKeyByValue(OrganizationStatus, OrganizationStatus.accessRevoked)!,
    [OrganizationStatus.deleted]: getEnumKeyByValue(OrganizationStatus, OrganizationStatus.deleted)!
};

export interface PersonalInfo {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    jobTitle?: string;
}

interface OrganizationApi {
    npi?: string;
    city: string;
    name: string;
    state: string;
    userId?: string;
    zipCode: string;
    addressLine1: string;
    addressLine2?: string;
    details?: string;
    country?: string;
    contactPhoneNumber?: string;
    contactJobTitle?: string;
}

export interface OrganizationApiResponse {
    id: number;
    npi: string;
    name: string;
    details?: string;
    contactEmail: string;
    contactFirstName: string;
    contactLastName: string;
    contactPhoneNumber?: string;
    contactJobTitle?: string;
    addressLine1: string;
    addressLine2: string;
    addressCity: string;
    addressState: string;
    addressZipCode: string;
    addressCountry?: string;
    status: OrganizationStatus;
    administratorId: string;
    administratorFirstName: string;
    administratorLastName: string;
    administratorEmail: string;
    administratorJobTitle?: string;
    administratorPhoneNumber?: string;
}

export const organizationToApi = (org: Organization): Partial<OrganizationApi> => {
    const parseOrg: Partial<OrganizationApi> = {
        city: org.address.city,
        name: org.orgName,
        state: org.address.stateCode,
        addressLine1: org.address.streetAddress,
        zipCode: org.address.postalCode,
        country: org.address.country
    };
    if (org.npiNumber) parseOrg.npi = org.npiNumber?.toString();
    if (org.userId) parseOrg.userId = org.userId;
    if (org.address.streetAddress2) parseOrg.addressLine2 = org.address.streetAddress2;
    if (org.details) parseOrg.details = org.details;
    if (org.primaryContact?.phoneNumber) parseOrg.contactPhoneNumber = org.primaryContact?.phoneNumber;
    if (org.primaryContact?.jobTitle) parseOrg.contactJobTitle = org.primaryContact?.jobTitle;

    return parseOrg;
};

export const apiOrganizationToOrganization = (org: OrganizationApiResponse): Organization => {
    return {
        id: org.id,
        npiNumber: org.npi,
        orgName: org.name,
        details: org.details,
        primaryContact: {
            firstName: org.contactFirstName,
            lastName: org.contactLastName,
            email: org.contactEmail,
            phoneNumber: org.contactPhoneNumber,
            jobTitle: org.contactJobTitle
        },
        administrator: {
            firstName: org.administratorFirstName,
            lastName: org.administratorLastName,
            email: org.administratorEmail,
            phoneNumber: org.administratorPhoneNumber,
            jobTitle: org.administratorJobTitle
        },
        address: {
            city: org.addressCity,
            streetAddress: org.addressLine1,
            streetAddress2: org.addressLine2,
            stateCode: org.addressState,
            postalCode: org.addressZipCode,
            country: org.addressCountry
        },
        status: org.status
    };
};

export const getRolesFromLocations = (locations: UserLocation[] | undefined): string[] => {
    const roles: string[] = [];
    if (locations && locations.length > 0) {
        locations.forEach((l) => {
            if (l.roles && l.roles.length > 0) {
                l.roles.forEach((r) => {
                    if (!roles.includes(r.name)) roles.push(r.name);
                });
            }
        });
    }

    return roles;
};

export const getReinstateRevokeToolTipText = (status: OrganizationStatus): string => {
    return (status as string) === getEnumKeyByValue(OrganizationStatus, OrganizationStatus.accessRevoked)
        ? "By reinstating access you are restoring full access to the organization including all existing users and unused assessments."
        : "By revoking access you lock platform access to all organization users. All assessments are also locked and cannot be accessed. After you revoke access, you can reinstate access here in this menu.";
};
